import { useCallback, useEffect, useMemo, useState } from "react";
import useBillingApi from "../api/use-billing-api";
import type {
  BillingPlanId,
  BillingCheckoutConfig,
  PaddleCheckoutSettings,
} from "../types/billing";
import { initializePaddle } from "../utils/paddle-client";
import { trackEvent } from "../utils/firebase-analytics";

const CHECKOUT_SETTINGS: PaddleCheckoutSettings = {
  displayMode: "overlay",
  theme: "light",
  locale: "en",
};

const usePaddleCheckout = () => {
  const { createCheckoutTransaction, getCheckoutConfig } = useBillingApi();
  const [isOpeningCheckout, setIsOpeningCheckout] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [checkoutConfig, setCheckoutConfig] =
    useState<BillingCheckoutConfig | null>(null);

  const refreshCheckoutConfig = useCallback(async () => {
    setIsLoadingConfig(true);
    try {
      const nextConfig = await getCheckoutConfig();
      setCheckoutConfig(nextConfig);
      return nextConfig;
    } finally {
      setIsLoadingConfig(false);
    }
  }, [getCheckoutConfig]);

  useEffect(() => {
    void refreshCheckoutConfig();
  }, [refreshCheckoutConfig]);

  const openCheckout = useCallback(
    async (planId: BillingPlanId = "pro_monthly"): Promise<boolean> => {
      trackEvent("checkout_started", {
        plan_id: planId,
      });
      const activeConfig = checkoutConfig ?? (await refreshCheckoutConfig());
      if (activeConfig?.betaEnabled) {
        trackEvent("checkout_blocked", {
          plan_id: planId,
          reason: "beta_enabled",
        });
        return false;
      }

      if (
        !activeConfig?.configured ||
        !activeConfig.clientToken ||
        !activeConfig.pricesConfigured[planId]
      ) {
        console.error("Missing server-side Paddle checkout configuration.");
        trackEvent("checkout_blocked", {
          plan_id: planId,
          reason: "missing_configuration",
        });
        return false;
      }

      setIsOpeningCheckout(true);

      try {
        const transaction = await createCheckoutTransaction(planId);
        if (!transaction?.transactionId) {
          return false;
        }

        await initializePaddle(
          transaction.clientToken || activeConfig.clientToken,
          transaction.environment || activeConfig.environment,
        );

        if (!window.Paddle) {
          throw new Error("Paddle Checkout is unavailable.");
        }

        window.Paddle.Checkout.open({
          transactionId: transaction.transactionId,
          settings: CHECKOUT_SETTINGS,
        });

        trackEvent("checkout_opened", {
          plan_id: planId,
          environment: transaction.environment || activeConfig.environment,
        });
        return true;
      } catch (error) {
        console.error(
          "Failed to open Paddle checkout",
          error instanceof Error ? error.message : error,
        );
        trackEvent("checkout_failed", {
          plan_id: planId,
        });
        return false;
      } finally {
        setIsOpeningCheckout(false);
      }
    },
    [checkoutConfig, createCheckoutTransaction, refreshCheckoutConfig],
  );

  return useMemo(
    () => ({
      openCheckout,
      isOpeningCheckout,
      isConfigured: Boolean(checkoutConfig?.configured),
      pricesConfigured: checkoutConfig?.pricesConfigured ?? {
        pro_monthly: false,
        agency_monthly: false,
      },
      betaEnabled: Boolean(checkoutConfig?.betaEnabled),
      isLoadingConfig,
      environment: checkoutConfig?.environment ?? "sandbox",
    }),
    [
      openCheckout,
      isOpeningCheckout,
      checkoutConfig?.configured,
      checkoutConfig?.pricesConfigured,
      checkoutConfig?.betaEnabled,
      checkoutConfig?.environment,
      isLoadingConfig,
    ],
  );
};

export default usePaddleCheckout;
